//@ts-check
import React, { useState, useEffect, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/react'
import { PitchBg, colors, fonts } from 'shared-ui'

/**
 * @param {string} hsl  e.g. "hsl(222, 60%, 62%)"
 * @param {number} alpha
 */
function hslToHsla(hsl, alpha) {
    return hsl.replace('hsl(', 'hsla(').replace(')', `, ${alpha})`)
}

export default function DrillSectionSelector({
    backgroundType,
    rows = 1,
    columns = 1,
    drillTypes,
    drillTypeColors,
    sections,
    onChange,
    onLoadPrevious,
    loadingPrevious = false,
}) {
    const { formatMessage } = useIntl()

    const [selectedDrill, setSelectedDrill] = useState(
        /** @type {string | null} */ (drillTypes[0]?.[0] ?? null)
    )

    const [sectionState, setSectionState] = useState(
        /** @type {(string | null)[][]} */
        (sections.length ? sections : new Array(rows).fill(null).map(() => new Array(columns).fill(null)))
    )

    useEffect(() => {
        if (sections.length) setSectionState(sections)
    }, [sections])

    useEffect(() => {
        onChange(sectionState)
    }, [sectionState])

    const handleZoneClick = useCallback((i, j) => {
        setSectionState(prevGrid => {
            const newGrid = prevGrid.map(row => [...row])
            newGrid[i][j] = prevGrid[i][j] === selectedDrill ? null : selectedDrill
            return newGrid
        })
    }, [selectedDrill])

    const handleClearAll = useCallback(() => {
        setSectionState(prev => prev.map(row => row.map(() => null)))
    }, [])

    return (
        <div css={css`
            display: flex;
            flex-direction: row;
            gap: 1em;
            align-items: flex-start;

            @media screen and (max-width: 800px) {
                flex-direction: column-reverse;
            }
        `}>
            <div css={css`display: flex; flex-direction: column;`}>
                <div css={css`width: 100%; display: flex; justify-content: center; gap: 1px;`}>
                    {onLoadPrevious &&
                        <button onClick={onLoadPrevious} disabled={loadingPrevious} css={[style.clearButton, style.clearButtonLeft]}>
                            {formatMessage({ id: 'loadPrevious' })}
                        </button>
                    }
                    <button onClick={handleClearAll} css={[style.clearButton, onLoadPrevious ? style.clearButtonRight : style.clearButtonFull]}>
                        {formatMessage({ id: 'clearDataGeneric' })}
                    </button>
                </div>
                <div css={style.fieldWrapper}>
                    <PitchBg type={backgroundType} css={style.field} />
                    <div css={style.field}>
                        {sectionState.map((r, i) => (
                            <div key={i} css={style.column(rows, columns, selectedDrill ? drillTypeColors[selectedDrill] : colors.main1)}>
                                {r.map((drill, j) => (
                                    <div
                                        key={`${i}-${j}`}
                                        className="section"
                                        style={drill ? {
                                            background: hslToHsla(drillTypeColors[drill], 0.35),
                                            borderColor: drillTypeColors[drill],
                                            position: 'relative',
                                        } : {}}
                                        onClick={() => handleZoneClick(i, j)}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div css={css`
                display: flex;
                flex-direction: column;
                flex-wrap: wrap;
                gap: 0.5em;
                margin-top: 2em;

                @media screen and (max-width: 800px) {
                    flex-direction: row;
                }
            `}>
                {drillTypes.map(([codename, label]) => (
                    <DrillTypeCard
                        key={codename}
                        label={label}
                        color={drillTypeColors[codename]}
                        selected={codename === selectedDrill}
                        onClick={() => setSelectedDrill(codename)}
                    />
                ))}
            </div>
        </div>
    )
}

function DrillTypeCard({ label, color, selected, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            css={css`
                background: ${colors.eventDark};
                border: 2px solid ${selected ? color : colors.eventLight};
                border-radius: 5px;
                padding: 8px 16px;
                cursor: pointer;
                user-select: none;
                -webkit-user-select: none;
                color: ${selected ? color : colors.soft};
                outline: none;
                position: relative;
                overflow: hidden;
                text-align: left;
                &:hover { border-color: ${color}; color: ${color}; }
            `}
        >
            {label}
            <div css={css`
                position: absolute;
                bottom: 0; left: 0; right: 0;
                height: 3px;
                background: ${color};
                opacity: ${selected ? 1 : 0.4};
            `} />
        </button>
    )
}

const style = {
    clearButtonLeft: css`
        border-top-left-radius: 3px;
        border-top-right-radius: 0;
    `,
    clearButtonRight: css`
        border-top-left-radius: 0;
        border-top-right-radius: 3px;
    `,
    clearButtonFull: css`
        border-top-left-radius: 3px;
        border-top-right-radius: 3px;
    `,
    clearButton: css`
        background: ${colors.eventLight};
        border-radius: 0;
        color: ${colors.soft};
        font-family: ${fonts.main};
        border: none;
        padding: 0.4em 0.8em;
        cursor: pointer;
        &:disabled { opacity: 0.5; cursor: default; }

        &:hover {
            color: ${colors.white};
            background: ${colors.eventDark};
        }

        &:active {
            color: ${colors.soft};
            background: ${colors.eventLight};
        }
    `,
    fieldWrapper: css`
        background-color: #363E45;
        width: 450px;
        height: 300px;
        box-shadow: 0px 0px 1px 2px ${colors.eventLight};
        position: relative;
        overflow: hidden;
        display: flex;
        flex-direction: column;

        @media screen and (max-width: 600px) {
            width: 225px;
            height: 150px;
        }
    `,
    field: css`
        width: 450px;
        height: 300px;
        position: absolute;

        @media screen and (max-width: 600px) {
            width: 225px;
            height: 150px;
        }
    `,
    column: (/** @type {number} */ rows, /** @type {number} */ columns, /** @type {string} */ hoverColor) => css`
        display: flex;
        flex-direction: row;
        margin-bottom: -1px;
        .section {
            width: calc((450px + ${columns - 1}px) / ${columns});
            height: calc((300px + ${rows - 1}px) / ${rows});

            @media screen and (max-width: 600px) {
                width: calc((225px + ${columns - 1}px) / ${columns});
                height: calc((150px + ${rows - 1}px) / ${rows});
            }

            border: 2px solid ${colors.eventDark};
            cursor: pointer;
            margin-left: -1px;

            &:hover {
                border: 2px solid ${hoverColor};
                position: relative;
            }

            &:first-of-type {
                margin-left: 0;
            }
        }
    `,
}
